import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ChevronRightIcon } from 'lucide-react';
import { services } from '../data/services';
import { sectorPages } from '../data/sectorPages';
import { werkgebiedPlaces } from '../data/werkgebied';
import { clientCases } from '../data/allClientCases';
import { articles, getAllClusters } from '../data/blogArticles';
import { blogPosts, getBlogCategories } from '../data/allBlogPosts';
import { buildBreadcrumbList, buildCollectionSchema } from '../lib/structuredData';
interface SitemapLink {
  label: string;
  path: string;
  external?: boolean;
}
const teamLinks: SitemapLink[] = [{
  label: 'Rocco',
  path: '/over-ons/rocco'
}, {
  label: 'Beer',
  path: '/over-ons/beer'
}, {
  label: 'Alex',
  path: '/over-ons/alex'
}, {
  label: 'Loïs Bruins',
  path: '/over-ons/lois'
}, {
  label: 'Jesse Put',
  path: '/over-ons/jesse'
}, {
  label: 'Norden',
  path: '/over-ons/norden'
}];
const bedrijfLinks: SitemapLink[] = [{
  label: 'Over ons',
  path: '/over-ons'
}, {
  label: 'Bibliotheek',
  path: '/bibliotheek'
}, {
  label: 'Prijsscan',
  path: '/prijsscan'
}, {
  label: 'Werken bij Noord Digital',
  path: '/sollicitatie'
}];
const juridischLinks: SitemapLink[] = [{
  label: 'Algemene voorwaarden',
  path: '/algemene-voorwaarden'
}, {
  label: 'Privacybeleid',
  path: '/privacy'
}, {
  label: 'Cookiebeleid',
  path: '/cookiebeleid'
}];
const LinkList: React.FC<{
  links: SitemapLink[];
}> = ({
  links
}) => <ul className="space-y-2.5" data-id="element-2534" data-mp="fxq3rv9ogvfn">
    {links.map(link => <li key={`${link.label}-${link.path}`} data-id="element-2535" data-mp="fw5bydeqzqlh">
        {link.external ? <a href={link.path} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-2 text-[#4B5563] hover:text-[#004AC7] transition-colors duration-200" data-id="element-2536" data-mp="jytmqqn0v7l3">
            <ChevronRightIcon className="h-4 w-4 text-gray-300 mt-1 flex-shrink-0" aria-hidden="true" data-id="element-2537" data-mp="2lqg4e1l0xq4w" />
            <span data-id="element-2538" data-mp="3p1c4wbpe129b">{link.label}</span>
          </a> : <Link to={link.path} className="inline-flex items-start gap-2 text-[#4B5563] hover:text-[#004AC7] transition-colors duration-200" data-id="element-2539" data-mp="3p2do53gk4v24">
            <ChevronRightIcon className="h-4 w-4 text-gray-300 mt-1 flex-shrink-0" aria-hidden="true" data-id="element-2540" data-mp="1xxjpyij9ji8" />
            <span data-id="element-2541" data-mp="3u0p0bkubrhni">{link.label}</span>
          </Link>}
      </li>)}
  </ul>;
export const SitemapPage: React.FC = () => {
  const serviceLinks: SitemapLink[] = services.map(service => ({
    label: service.title,
    path: service.link,
    external: service.external
  }));
  const sectorLinks: SitemapLink[] = sectorPages.map(page => ({
    label: page.sector,
    path: `/website-voor/${page.slug}`
  }));
  const werkgebiedLinks: SitemapLink[] = [{
    label: 'Overzicht werkgebied',
    path: '/werkgebied'
  }, ...werkgebiedPlaces.map(place => ({
    label: place.name,
    path: `/werkgebied/${place.slug}`
  }))];
  const caseLinks: SitemapLink[] = clientCases.map(clientCase => ({
    label: clientCase.clientName,
    path: `/cases/${clientCase.slug}`
  }));
  const clusters = getAllClusters();
  const blogCategories = getBlogCategories();
  const collectionItems = [...serviceLinks.filter(link => !link.external), ...sectorLinks, ...werkgebiedLinks, ...caseLinks, ...blogPosts.map(post => ({
    label: post.title,
    path: `/blog/${post.slug}`
  }))].map(link => ({
    name: link.label,
    path: link.path
  }));
  const sections: {
    title: string;
    intro: string;
    links: SitemapLink[];
  }[] = [{
    title: 'Diensten',
    intro: 'Wat we doen, van website tot methodiek.',
    links: serviceLinks
  }, {
    title: 'Website per branche',
    intro: 'Wat er per sector op een website thuishoort.',
    links: sectorLinks
  }, {
    title: 'Werkgebied',
    intro: 'Hoe we werken in Alkmaar en de rest van Noord-Holland Noord.',
    links: werkgebiedLinks
  }, {
    title: 'Klantcases',
    intro: 'Werk dat we hebben opgeleverd, met de aanpak erbij.',
    links: caseLinks
  }, {
    title: 'Bedrijf',
    intro: 'Wie we zijn en wat je zelf kunt uitzoeken.',
    links: bedrijfLinks
  }, {
    title: 'Team',
    intro: 'De mensen met wie je direct schakelt.',
    links: teamLinks
  }, {
    title: 'Juridisch',
    intro: 'Voorwaarden en hoe we met gegevens omgaan.',
    links: juridischLinks
  }];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-2542" data-mp="3mv1jddabfuf8">
      <SEO title="Alle pagina's op een rij, de sitemap | Noord Digital" description="Een overzicht van alle pagina's van Noord Digital: diensten, sectorpagina's, klantcases, kennisartikelen over SEO en de informatie over ons bedrijf." keywords="sitemap noord digital, overzicht pagina's, alle diensten noord digital" canonical="https://noorddigital.nl/sitemap" jsonLd={[buildCollectionSchema({
      name: "Sitemap van Noord Digital",
      description: "Overzicht van alle pagina's van Noord Digital, van diensten en sectorpagina's tot klantcases en kennisartikelen.",
      path: '/sitemap',
      items: collectionItems
    }), buildBreadcrumbList([{
      name: 'Sitemap',
      path: '/sitemap'
    }])]} data-id="element-2543" data-mp="3hwzziq52fr2p" />
      <Header data-id="element-2544" data-mp="2p1a2d1bkqgwr" />

      <main data-id="element-2545" data-mp="2p0qtea3wj2uv">
        <section className="w-full bg-[#F7F8FA] pt-32 pb-14 md:pt-40 md:pb-16 border-b border-gray-200" data-id="element-2546" data-mp="2p0794mqs42do">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2547" data-mp="2tv4p6dmh7c02">
            <nav aria-label="Kruimelpad" className="mb-6" data-id="element-2548" data-mp="2xtu1e2vs2ni4">
              <ol className="flex items-center gap-1.5 text-sm text-[#6B7280]" data-id="element-2549" data-mp="2154kofvw00xq">
                <li data-id="element-2550" data-mp="26361eu5ubo61">
                  <Link to="/" className="hover:text-[#004AC7] transition-colors duration-200" data-id="element-2551" data-mp="262o0hk88glrs">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" data-id="element-2552" data-mp="2f44a3pr84juy">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300" data-id="element-2553" data-mp="2f56vxj9ovw40" />
                </li>
                <li className="font-medium text-[#111827]" aria-current="page" data-id="element-2554" data-mp="3lj6a71n70tjw">
                  Sitemap
                </li>
              </ol>
            </nav>
            <h1 className="font-bold text-[#111827] text-4xl md:text-5xl leading-tight mb-5 max-w-3xl" data-id="element-2555" data-mp="1qh1f4zlipdp2">
              Alle pagina's van Noord Digital op een rij
            </h1>
            <p className="text-[#4B5563] text-lg leading-relaxed max-w-2xl" data-id="element-2556" data-mp="1mhiv061it6na">
              Zoek je iets specifieks, dan vind je het hier sneller dan via het
              menu. Ook handig als je wilt zien voor welke branches we een eigen
              pagina hebben.
            </p>
          </div>
        </section>

        <section className="w-full bg-white py-16 md:py-20" data-id="element-2557" data-mp="12rgxck13omhd">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2558" data-mp="12t1rw9nf05f8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12" data-id="element-2559" data-mp="1hh4y9k5ddj4k">
              {sections.map(section => <div key={section.title} data-id="element-2560" data-mp="1cobt4v5zo8ry">
                  <h2 className="font-bold text-[#111827] text-xl mb-2" data-id="element-2561" data-mp="ozsn6maew831">
                    {section.title}
                  </h2>
                  <p className="text-sm text-[#6B7280] leading-relaxed mb-5" data-id="element-2562" data-mp="jzxr84bs4sb4">
                    {section.intro}
                  </p>
                  <LinkList links={section.links} data-id="element-2563" data-mp="2vm84aih0r3md" />
                </div>)}
            </div>
          </div>
        </section>

        <section className="w-full bg-[#F7F8FA] py-16 md:py-20 border-t border-gray-200" data-id="element-2564" data-mp="2mnmkyl3q3kkj">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2565" data-mp="2qp7vhzssbmrk">
            <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-3" data-id="element-2566" data-mp="2qn59dqur6ds4">
              Blogartikelen
            </h2>
            <p className="text-[#6B7280] leading-relaxed mb-10 max-w-2xl" data-id="element-2567" data-mp="3jdn9cj06jz1b">
              {blogPosts.length} praktische stukken over vindbaarheid,
              websites, vormgeving en beheer, verdeeld over{' '}
              {blogCategories.length} onderwerpen.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12" data-id="element-2568" data-mp="3af1uoyxtx45t">
              {blogCategories.map(category => <div key={category} data-id="element-2569" data-mp="1yxs9a6v5421v">
                  <h3 className="font-semibold text-[#111827] mb-4" data-id="element-2570" data-mp="1yytqyu6nvmsk">
                    {category}
                  </h3>
                  <LinkList links={blogPosts.filter(post => post.category === category).map(post => ({
                label: post.title,
                path: `/blog/${post.slug}`
              }))} data-id="element-2571" data-mp="2pm0z2nabsb03" />
                </div>)}
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-16 md:py-20" data-id="element-2572" data-mp="2bpns483f3rzv">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2573" data-mp="39buvjenzjean">
            <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-3" data-id="element-2574" data-mp="34d1itogivrbj">
              SEO-kennisbank
            </h2>
            <p className="text-[#6B7280] leading-relaxed mb-10 max-w-2xl" data-id="element-2575" data-mp="2zljhv0ei1qqk">
              {articles.length} artikelen, verdeeld over {clusters.length}{' '}
              onderwerpen. Uitleg zonder jargon, bedoeld om zelf betere keuzes te
              kunnen maken.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12" data-id="element-2576" data-mp="3nc5qotwbvf1m">
              {clusters.map(cluster => <div key={cluster} data-id="element-2577" data-mp="3untwdrusha47">
                  <h3 className="font-semibold text-[#111827] mb-4" data-id="element-2578" data-mp="3uov7fwbt1wlx">
                    {cluster}
                  </h3>
                  <LinkList links={articles.filter(article => article.cluster === cluster).map(article => ({
                label: article.title,
                path: `/seo/blog/${article.slug}`
              }))} data-id="element-2579" data-mp="3ikb2dhqpk0x" />
                </div>)}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2580" data-mp="henztr9uh060" />
    </div>;
};